import { ChipIconButtonRed } from '@components/chip-icon-button';
import { ColoredButton, ColoredChip, ColoredFab } from '@components/colored-elements';
import { TabPanel } from '@components/tab-panel';
import { AiOutlinePlusCircle, FaFilter } from '@icon/react-icons';
import { rbac, useBoolean, useRecentGetter, useRecentInjector } from '@lib/common';
import { thumbStack } from '@lib/common/css';
import {
  Divider, Grid, Popover,
  PopoverOrigin,
  Tab,
  Tabs, TextField, Theme, Tooltip
} from '@material-ui/core';
import { green, purple } from '@material-ui/core/colors';
import { makeStyles } from '@material-ui/core/styles';
import { ClearAll } from '@material-ui/icons';
import SearchIcon from '@material-ui/icons/Search';
import { get } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { operatorByKey } from '../filters/operators';

const CompanyFilters = styled.div`
  .MuiTabs-flexContainer.MuiTabs-flexContainerVertical {
    // max-height: 80vh;
    min-height: 94vh;
    overflow-y: scroll;
    ${thumbStack}
  }
`;

const SearchBox = props => {
  const { columns } = props;

  const primaryFilterableColumns = useMemo(() => columns.filter(s => s.primaryFilter), [columns]);

  const { handleSubmit, ...form } = useForm();

  const submitForm = useRecentInjector(primaryFilterableColumns as any[], (cols, values) => {
    form.setValue('value', '');
    const column = cols.find(c => values.id === c.id);
    column.setFilter({
      ...column.filterValue,
      [column.primaryFilterOperator]: values.value,
    });
  });

  const submit = useMemo(() => handleSubmit(submitForm), []);
  return (
    <Grid container spacing={2}>
      <Grid item>
        <AddFilter columns={props.filterableColumns} />
      </Grid>
      <Grid item xs component="form" onSubmit={submit}>
        <Grid container spacing={2}>
          <Grid item container xs>
            <Grid item>
              <TextField
                variant="outlined"
                size="small"
                name="id"
                inputRef={form.register}
                select
                SelectProps={{
                  native: true,
                }}>
                {primaryFilterableColumns.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.render('Header')}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs>
              <TextField
                name="value"
                inputRef={form.register}
                required
                placeholder="Search"
                fullWidth
                size="small"
                variant="outlined"
              />
            </Grid>
          </Grid>
          <Grid item>
            <ColoredFab type="submit" colored={purple[700]} coloredHover={purple[900]} size="small">
              <SearchIcon />
            </ColoredFab>
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <ColoredButton
          colored={green[700]}
          coloredHover={green[900]}
          component="a"
          variant="contained"
          startIcon={<AiOutlinePlusCircle />}
          disabled={rbac("C2P_New", "", "Button")}
          href={`/company-person/add`}>
          Add
        </ColoredButton>
      </Grid>
    </Grid>
  );
};

const FilterBox = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px 5px;
  padding: 10px 0;
`;

const TableToolbar = props => {
  const { columns, filters, setFilter, setAllFilters } = props;
  const filterableColumns = useMemo(
    () => columns.filter(c => c.canFilter || c.passiveColumn),
    [columns]
  );

  return (
    <>
      <div style={{ padding: 10 }}>
        <SearchBox {...props} filterableColumns={filterableColumns} />
      </div>
      <Divider />
      {filters && filters.length ? (
        <>
          Showing results for,
          <FilterBox>
            <FilterChips
              setAllFilters={setAllFilters}
              filters={filters}
              columns={columns}
              setFilter={setFilter}
            />
          </FilterBox>
        </>
      ) : null}
      <Divider />
    </>
  );
};
export default TableToolbar;

const DefaultLabel = ({ label, value, operatorLabel }) => (
  <>
    {label} {operatorLabel} {value}
  </>
);

const FilterChip = styled(({ className = null, label, remove, opeartor, dataKey }) => {
  const getOperator = useRecentGetter(opeartor);
  const getDatakey = useRecentGetter(dataKey);
  const getRemove = useRecentGetter(remove);
  const onDelete = useCallback(() => {
    getRemove()(getOperator(), getDatakey());
  }, []);
  return (
    <Tooltip title={label}>
      <ColoredChip
        className={className}
        colored={purple[900]}
        size="small"
        label={label}
        onDelete={onDelete}
      />
    </Tooltip>
  );
})`
  max-width: 300px;
`;

const FilterChips = ({ filters, columns, setFilter, setAllFilters }) => {
 // console.log(filters)
  const columnsById = useMemo(
    () => columns.reduce((acc, c) => ({ ...acc, [c.id]: c }), {}),
    [columns]
  );

  const clearFilters = useCallback(() => setAllFilters([]), []);

  const getFilters = useRecentGetter(filters);

  const removeFilter = useCallback((op, key) => {
    const filter = getFilters().find(f => f.id === key);
    if (filter) {
      delete filter.value[op];
      setFilter(filter.id, !Object.keys(filter.value).length ? undefined : { ...filter.value });
    }
  }, []);

  useEffect(() => {
    const filter = filters.find(f => f.id === 'currently_working');

    if (filter?.value?.$eq && filter.value.$notlike) {
      setFilter('currently_working', { $eq: filter.value.$eq });
    }
  }, [filters]);

  return (
    <>
      {(filters as any[]).map(({ id: key, value: ops }) => {
        const Header = columnsById[key].render('Header');
        return (
          <React.Fragment key={key}>
            {Object.entries(ops).map(([operator, value]) => {
              const defination = {
                ...operatorByKey[operator as string],
                ...get(columnsById, [key, 'overrideOperator', operator], {}),
              };
              const RenderLabel = defination.ChipLabel || DefaultLabel;
              return (
                <React.Fragment key={operator}>
                  <FilterChip
                    remove={removeFilter}
                    opeartor={operator}
                    dataKey={key}
                    label={
                      <RenderLabel
                        label={Header}
                        value={value}
                        operatorLabel={defination.operatorLabel}
                      />
                    }
                  />{' '}
                </React.Fragment>
              );
            })}
          </React.Fragment>
        );
      })}
      {filters.length > 0 && (
        <>
          <ChipIconButtonRed size="small" icon={<ClearAll />} onClick={clearFilters} />{' '}
        </>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    // height: 224,
  },
  tabs: {
    borderRight: `1px solid ${theme.palette.divider}`,
  },
}));

const popoverOpts = {
  anchor: {
    vertical: 'bottom',
    horizontal: 'right',
  } as PopoverOrigin,
  transform: {
    vertical: 'top',
    horizontal: 'left',
  } as PopoverOrigin,
};

function AddFilter({ columns }) {
  const divRef = useRef<HTMLDivElement>();
  const classes = useStyles();
  const [filterOpen, openFilter, closeFilter] = useBoolean(false);
  const [currentTab, setCurrentTab] = useState(columns && columns.length ? columns[0].id : null);
  const handleTabChange = useCallback((e, t) => setCurrentTab(t), [setCurrentTab]);
  return (
    <div ref={divRef} style={{ display: 'inline-block' }}>
      {/* <ChipIconButton color="primary" size="small" icon={<AddIcon />} onClick={openFilter} /> */}
      <ColoredFab
        colored={purple[700]}
        coloredHover={purple[900]}
        size="small"
        onClick={openFilter}>
        <FaFilter fontSize="inherit" />
      </ColoredFab>
      <Popover
        anchorEl={divRef.current}
        anchorOrigin={popoverOpts.anchor}
        transformOrigin={popoverOpts.transform}
        transitionDuration={0}
        open={filterOpen}
        onClose={closeFilter}>
        <div className={classes.root}>
          <CompanyFilters>
            <Tabs
              orientation="vertical"
              className={classes.tabs}
              variant="scrollable"
              value={currentTab}
              onChange={handleTabChange}>
              {columns.map(c => (
                <Tab key={c.id} value={c.id} label={c.render('Header')} />
              ))}
            </Tabs>
          </CompanyFilters>
          {columns.map(c => (
            <TabPanel value={currentTab} key={c.id} index={c.id}>
              {c.render('Filter', { openFilter, closeFilter })}
            </TabPanel>
          ))}
        </div>
      </Popover>
    </div>
  );
}
